import ClubIcon from "@/assets/club.svg";
import HeartIcon from "@/assets/heart.svg";
import Logout from "@/assets/logout.svg";
import NewMessagesIcon from "@/assets/msg-new.svg";
import MessagesNoneIcon from "@/assets/msg-none.svg";
import ProfileIcon from "@/assets/profile.svg";
import SearchIcon from "@/assets/search.svg";
import SettingsIcon from "@/assets/settings.svg";
import TeamzIcon from "@/assets/teamz.svg";
import TournamentsIcon from "@/assets/tournaments.svg";
import { useClerk } from "@clerk/react";
import { ShieldUser, Zap } from "lucide-react";
import { useState } from "react";
import { CircleFlag } from "react-circle-flags";
import { useTranslation } from "react-i18next";
import { Link, NavLink } from "react-router";

import { Button } from "@/components/ui/button";
import { useMe } from "@/context/user-context";
import { useHasUnreadMessages } from "@/hooks/use-has-unread-msg";
import { cn, toggleLanguage } from "@/lib/utils";
import { routes } from "@/routes";

import { LogoutConfirmationModal } from "./nav-mobile";
import { navLinkVariants } from "./navlink.styles";

export function NavDesktop() {
  const { user } = useMe();
  const { openUserProfile, signOut } = useClerk();
  const { t, i18n } = useTranslation(["common"]);
  const [modalState, updateModalState] = useState<"open" | "close">("close");
  const { hasUnread } = useHasUnreadMessages();

  const playerNavlinks = (
    <>
      {user.isAdmin && (
        <NavLink
          to={routes.admin}
          className={({ isActive }) => navLinkVariants({ state: isActive ? "active" : null })}
        >
          <Button variant="nav-link">
            <ShieldUser strokeWidth={1.5} />
            Admin
          </Button>
        </NavLink>
      )}

      <NavLink
        to={routes.clubs}
        className={({ isActive }) => navLinkVariants({ state: isActive ? "active" : null })}
      >
        <Button variant="nav-link">
          <SearchIcon />
          {t("common:desktop-nav.clubs")}
        </Button>
      </NavLink>

      <NavLink
        to={routes.tournaments}
        className={({ isActive }) => navLinkVariants({ state: isActive ? "active" : null })}
      >
        <Button variant="nav-link">
          <TournamentsIcon />
          {t("common:desktop-nav.tournaments")}
        </Button>
      </NavLink>

      <NavLink
        to={routes.favorites}
        className={({ isActive }) => navLinkVariants({ state: isActive ? "active" : null })}
      >
        <Button variant="nav-link">
          <HeartIcon />
          {t("common:desktop-nav.favorites")}
        </Button>
      </NavLink>

      <NavLink to={routes.messages} className={({ isActive }) => navLinkVariants({ state: isActive ? "active" : null })}>
        <Button variant="nav-link">
          {hasUnread
            ? <NewMessagesIcon />
            : <MessagesNoneIcon />}
          {t("common:desktop-nav.messages")}
        </Button>
      </NavLink>

      <NavLink to={routes.profile} className={({ isActive }) => navLinkVariants({ state: isActive ? "active" : null })}>
        <Button variant="nav-link">
          <ProfileIcon />
          {t("common:desktop-nav.profile")}
        </Button>
      </NavLink>
    </>
  );

  const clubNavlinks = (
    <>
      {/* Joueurs reste ouvert aux clubs freemium : ils y voient la liste
          anonymisée, c'est le point d'entrée de la conversion. */}
      <NavLink
        to={routes.players}
        className={({ isActive }) => cn(navLinkVariants({ state: isActive ? "active" : null }), "relative")}
      >
        <Button variant="nav-link">
          <SearchIcon />
          {t("common:desktop-nav.players")}
        </Button>
      </NavLink>

      <NavLink
        to={routes.tournaments}
        className={({ isActive }) => cn(navLinkVariants({ state: isActive ? "active" : null }), "relative")}
      >
        <Button variant="nav-link">
          <TournamentsIcon />
          {t("common:desktop-nav.my-tournaments")}
        </Button>
      </NavLink>

      <NavLink
        to={routes.favorites}
        className={({ isActive }) => cn(navLinkVariants({ state: isActive ? "active" : null }), "relative")}
      >
        <Button variant="nav-link">
          <HeartIcon />
          {t("common:desktop-nav.favorites")}
        </Button>
      </NavLink>

      <NavLink to={routes.messages} className={({ isActive }) => navLinkVariants({ state: isActive ? "active" : null })}>
        <Button variant="nav-link">
          {hasUnread
            ? <NewMessagesIcon />
            : <MessagesNoneIcon />}
          {t("common:desktop-nav.messages")}
        </Button>
      </NavLink>

      <NavLink to={routes.profile} className={({ isActive }) => navLinkVariants({ state: isActive ? "active" : null })}>
        <Button variant="nav-link">
          <ClubIcon />
          {t("common:desktop-nav.my-club")}
        </Button>
      </NavLink>
    </>
  );

  return (
    <>
      <aside className="fixed inset-y-0 left-0 h-full p-4">
        <div className="flex h-full w-60 grow flex-col rounded-xl bg-white">
          <div className="flex h-[74px] items-center justify-between px-4">
            <Link to={user.role === "player" ? routes.clubs : routes.players}>
              <TeamzIcon className="h-8 w-14" />
            </Link>

            <Link to={routes.profile}>
              <img src={user.imgUrl} alt={`Photo de ${user.name}`} className="size-[34px] rounded-full object-cover" />
            </Link>
          </div>

          <div className="flex flex-col gap-2 p-2">
            {user.role === "player" && playerNavlinks}
            {user.role === "club" && clubNavlinks}
          </div>

          <div className="mt-auto  flex flex-col gap-2 p-2">
            <div className="h-px w-full bg-neutral-200"></div>

            {i18n.language === "en-US"
              ? (
                  <Button variant="nav-link" onClick={() => toggleLanguage(i18n)}>
                    <CircleFlag countryCode="fr" className="size-5 rounded-full" />
                    <span className="text-xs">Switch to French</span>
                  </Button>
                )
              : (
                  <Button variant="nav-link" onClick={() => toggleLanguage(i18n)}>
                    <CircleFlag countryCode="gb" className="size-5 rounded-full" />
                    <span className="text-xs">Switch to English</span>
                  </Button>
                )}

            <Button variant="nav-link" onClick={() => openUserProfile()}>
              <SettingsIcon />
              {t("common:settings")}
            </Button>

            {user.role === "club" && (

              <NavLink
                to={routes.plans}
                className={({ isActive }) => navLinkVariants({ state: isActive ? "active" : null })}
              >
                <Button variant="nav-link">
                  <Zap strokeWidth={1.5} />
                  {t("common:plan.plans-nav")}
                </Button>
              </NavLink>
            )}

            <Button
              variant="nav-link"
              onClick={() => updateModalState("open")}
            >
              <Logout />
              {t("common:logout")}
            </Button>
          </div>
        </div>
      </aside>

      <LogoutConfirmationModal
        modalOpen={modalState === "open"}
        closeModal={() => updateModalState("close")}
        signOut={() => signOut({
          redirectUrl: routes.signup,
        })}
      />
    </>
  );
}
